import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  HelpCircle, MessageSquare, Clock, CheckCircle, AlertTriangle,
  Search, Send, Loader2, XCircle, ChevronDown
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const statusConfig = {
  open: { label: 'Open', color: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/10 text-[#0075FF] border-blue-500/20', icon: AlertTriangle },
  resolved: { label: 'Resolved', color: 'bg-green-500/10 text-[#00CC99] border-green-500/20', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: XCircle },
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-slate-400' },
  normal: { label: 'Normal', color: 'text-[#0075FF]' },
  high: { label: 'High', color: 'text-[#FF6B6B]' },
  urgent: { label: 'Urgent', color: 'text-[#FF6B6B] font-bold' },
};

export default function MasterSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/master/support/tickets`, { headers });
      setTickets(res.data || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, []);

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/master/support/tickets/${ticketId}`, { status }, { headers });
      toast.success(`Ticket ${status.replace('_', ' ')}`);
      fetchTickets();
      if (selectedTicket?.id === ticketId) setSelectedTicket(prev => prev ? { ...prev, status } : prev);
    } catch { toast.error('Failed to update ticket'); }
  };

  const handleRespond = async () => {
    if (!responseText.trim() || !selectedTicket) return;
    setResponding(true);
    try {
      await axios.put(`${API}/master/support/tickets/${selectedTicket.id}`, {
        response: responseText.trim(),
        status: 'in_progress'
      }, { headers });
      toast.success('Response sent');
      setResponseText('');
      fetchTickets();
      setSelectedTicket(prev => prev ? { ...prev, response: responseText.trim(), status: 'in_progress' } : prev);
    } catch { toast.error('Failed to send response'); }
    finally { setResponding(false); }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) || t.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusTabs = [
    { key: 'all', label: 'All', count: tickets.length },
    { key: 'open', label: 'Open', count: tickets.filter(t => t.status === 'open').length },
    { key: 'in_progress', label: 'In Progress', count: tickets.filter(t => t.status === 'in_progress').length },
    { key: 'resolved', label: 'Resolved', count: tickets.filter(t => t.status === 'resolved').length },
  ];

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  return (
    <div className="space-y-6" data-testid="master-support">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="support-title">Help & Support Tickets</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">{tickets.length} total tickets from client sites</p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="ticket-status-tabs">
        {statusTabs.map((tab) => (
          <button key={tab.key} onClick={() => setStatusFilter(tab.key)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${statusFilter === tab.key
            ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]'
            : 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/10'
          }`} data-testid={`tab-${tab.key}`}>
            {tab.label} <span className="ml-1.5 text-xs opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search tickets..." className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="search-tickets" />
      </div>

      {filteredTickets.length > 0 ? (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => {
            const sc = statusConfig[ticket.status] || statusConfig.open;
            const pc = priorityConfig[ticket.priority] || priorityConfig.normal;
            return (
              <GlassCard key={ticket.id} className="p-5 cursor-pointer hover:border-[#8B5CF6]/20 transition-all" onClick={() => { setSelectedTicket(ticket); setResponseText(ticket.response || ''); }} data-testid={`ticket-${ticket.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <MessageSquare size={14} className="text-[#A78BFA] shrink-0" />
                      <h3 className="text-sm font-semibold text-white truncate">{ticket.subject}</h3>
                    </div>
                    <p className="text-xs text-[#94A3B8] line-clamp-2 mb-2">{ticket.message}</p>
                    <div className="flex items-center gap-3 text-xs text-[#64748B]">
                      <span>{ticket.user_email}</span>
                      <span>{ticket.site_id}</span>
                      <span>{formatDate(ticket.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-semibold border ${sc.color}`}>
                      <sc.icon size={10} /> {sc.label}
                    </span>
                    <span className={`text-[10px] font-medium ${pc.color}`}>{pc.label} Priority</span>
                    {ticket.response && <span className="text-[10px] text-[#00CC99]">Responded</span>}
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      ) : (
        <GlassCard className="text-center py-16">
          <HelpCircle size={48} className="mx-auto text-[#64748B] mb-4" />
          <h3 className="font-semibold text-lg text-white mb-2">No tickets</h3>
          <p className="text-[#9CA3AF] text-sm">{searchTerm ? 'No tickets match your search' : 'Support tickets from client sites will appear here.'}</p>
        </GlassCard>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">Ticket Details</DialogTitle></DialogHeader>
          {selectedTicket && (
            <div className="space-y-5" data-testid="ticket-detail">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white">{selectedTicket.subject}</h3>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-semibold border ${(statusConfig[selectedTicket.status] || statusConfig.open).color}`}>
                    {(statusConfig[selectedTicket.status] || statusConfig.open).label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-[#64748B] text-xs">From</p><p className="text-[#94A3B8]">{selectedTicket.user_email}</p></div>
                  <div><p className="text-[#64748B] text-xs">Site</p><p className="text-[#94A3B8]">{selectedTicket.site_id}</p></div>
                  <div><p className="text-[#64748B] text-xs">Priority</p><p className={(priorityConfig[selectedTicket.priority] || priorityConfig.normal).color}>{(priorityConfig[selectedTicket.priority] || priorityConfig.normal).label}</p></div>
                  <div><p className="text-[#64748B] text-xs">Created</p><p className="text-[#94A3B8]">{formatDate(selectedTicket.created_at)}</p></div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                <p className="text-xs text-[#64748B] mb-1.5 uppercase tracking-wider font-medium">Message</p>
                <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{selectedTicket.message}</p>
              </div>

              {selectedTicket.response && (
                <div className="p-4 rounded-xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20">
                  <p className="text-xs text-[#A78BFA] mb-1.5 uppercase tracking-wider font-medium">Your Response</p>
                  <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{selectedTicket.response}</p>
                  {selectedTicket.responded_at && <p className="text-[10px] text-[#64748B] mt-2">{formatDate(selectedTicket.responded_at)}</p>}
                </div>
              )}

              <div>
                <Label className="text-[#9CA3AF] text-xs">{selectedTicket.response ? 'Update Response' : 'Write Response'}</Label>
                <Textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={3} placeholder="Type your response to the client..." className="bg-white/5 border-white/10 text-white mt-1 rounded-xl" data-testid="ticket-response-input" />
                <button onClick={handleRespond} disabled={!responseText.trim() || responding} className="mt-2 w-full py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-40 transition-all text-sm flex items-center justify-center gap-2" data-testid="send-response-btn">
                  {responding ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {responding ? 'Sending...' : 'Send Response'}
                </button>
              </div>

              <div className="pt-3 border-t border-white/5">
                <p className="text-[#64748B] text-xs uppercase tracking-wider font-medium mb-2">Update Status</p>
                <div className="grid grid-cols-3 gap-2">
                  {['open', 'in_progress', 'resolved'].map((st) => {
                    const sc = statusConfig[st];
                    return (
                      <button key={st} onClick={() => updateTicketStatus(selectedTicket.id, st)} className={`py-2 rounded-xl text-xs font-medium border transition-colors ${selectedTicket.status === st ? sc.color + ' opacity-100' : 'bg-white/5 border-white/10 text-[#9CA3AF] hover:bg-white/10'}`} data-testid={`set-status-${st}`}>
                        {sc.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
